import { useEffect, useRef } from "react";
import * as THREE from "three";

const COUNT = 3000;

function buildShape({ segments = [], rings = [] }, offsetX = 0, scale = 1.5) {
  const lens = segments.map((s) => Math.hypot(s[2] - s[0], s[3] - s[1]));
  const ringLens = rings.map((r) => Math.PI * 2 * r.r);
  const total = lens.reduce((a, b) => a + b, 0) + ringLens.reduce((a, b) => a + b, 0);
  const pts = new Float32Array(COUNT * 3);
  for (let i = 0; i < COUNT; i++) {
    let d = Math.random() * total;
    let x = 0, y = 0, found = false;
    for (let s = 0; s < segments.length; s++) {
      if (d <= lens[s]) {
        const t = d / lens[s];
        x = segments[s][0] + (segments[s][2] - segments[s][0]) * t;
        y = segments[s][1] + (segments[s][3] - segments[s][1]) * t;
        found = true;
        break;
      }
      d -= lens[s];
    }
    if (!found) {
      for (let r = 0; r < rings.length; r++) {
        if (d <= ringLens[r]) {
          const a = (d / ringLens[r]) * Math.PI * 2;
          x = rings[r].cx + Math.cos(a) * rings[r].r;
          y = rings[r].cy + Math.sin(a) * rings[r].r;
          break;
        }
        d -= ringLens[r];
      }
    }
    pts[i * 3] = x * scale + offsetX + (Math.random() - 0.5) * 0.1;
    pts[i * 3 + 1] = y * scale + (Math.random() - 0.5) * 0.1;
    pts[i * 3 + 2] = (Math.random() - 0.5) * 0.7;
  }
  return pts;
}

const HOUSE = {
  segments: [
    [-1.5, 0.15, 0, 1.25], [0, 1.25, 1.5, 0.15],
    [-1.15, 0.15, -1.15, -1.25], [1.15, 0.15, 1.15, -1.25],
    [-1.15, -1.25, 1.15, -1.25], [-1.15, 0.15, 1.15, 0.15],
    [-0.25, -1.25, -0.25, -0.45], [0.25, -1.25, 0.25, -0.45], [-0.25, -0.45, 0.25, -0.45],
  ],
};
const COIN = {
  segments: [[0, -0.55, 0, 0.55]],
  rings: [{ cx: 0, cy: 0, r: 1.25 }, { cx: 0, cy: 0, r: 0.8 }],
};
const SHIELD = {
  segments: [
    [-1.15, 0.75, 0, 1.15], [0, 1.15, 1.15, 0.75],
    [1.15, 0.75, 1.0, -0.15], [1.0, -0.15, 0, -1.25],
    [0, -1.25, -1.0, -0.15], [-1.0, -0.15, -1.15, 0.75],
    [0, -0.4, 0, 0.4],
  ],
};
const ARROW = {
  segments: [
    [-1.6, -1.1, -0.6, -0.2], [-0.6, -0.2, 0, -0.6], [0, -0.6, 1.3, 0.9],
    [1.3, 0.9, 0.72, 0.84], [1.3, 0.9, 1.26, 0.32],
  ],
};

const smooth = (t) => t * t * (3 - 2 * t);

export const SectionParticles = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(max-width: 767px)").matches) return;
    const mount = mountRef.current;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 8;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    mount.appendChild(renderer.domElement);

    const shapes = [
      buildShape(HOUSE, 3.4),
      buildShape(COIN, -3.4),
      buildShape(SHIELD, 3.4),
      buildShape(ARROW, -3.2),
    ];

    const positions = new Float32Array(COUNT * 3);
    const sizes = new Float32Array(COUNT);
    const colors = new Float32Array(COUNT * 3);
    const phases = new Float32Array(COUNT);
    const speeds = new Float32Array(COUNT);
    const base = new THREE.Color("#FFFFFF");
    for (let i = 0; i < COUNT; i++) {
      const bright = 0.75 + Math.random() * 0.5;
      colors[i * 3] = base.r * bright;
      colors[i * 3 + 1] = base.g * bright;
      colors[i * 3 + 2] = base.b * bright;
      sizes[i] = 1.2 + Math.random() * 1.6;
      phases[i] = Math.random() * Math.PI * 2;
      speeds[i] = 0.5 + Math.random() * 1.3;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));
    geo.setAttribute("aColor", new THREE.BufferAttribute(colors, 3));

    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: { uOpacity: { value: 0 } },
      vertexShader: `
        attribute float aSize;
        attribute vec3 aColor;
        varying vec3 vColor;
        void main() {
          vColor = aColor;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = aSize * (11.0 / -mv.z);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        uniform float uOpacity;
        varying vec3 vColor;
        void main() {
          float d = length(gl_PointCoord - vec2(0.5));
          float a = smoothstep(0.5, 0.12, d);
          gl_FragColor = vec4(vColor, a * uOpacity);
        }`,
    });
    const points = new THREE.Points(geo, mat);
    scene.add(points);

    const clock = new THREE.Clock();
    let raf;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const t = clock.getElapsedTime();
      const vh = window.innerHeight;
      const maxScroll = Math.max(1, document.documentElement.scrollHeight - vh);
      const sy = window.scrollY;

      mat.uniforms.uOpacity.value = Math.min(Math.max((sy - vh * 0.6) / (vh * 0.6), 0), 1) * 0.55;
      if (mat.uniforms.uOpacity.value <= 0.001) {
        renderer.render(scene, camera);
        return;
      }

      const pp = Math.min(Math.max((sy - vh) / (maxScroll - vh), 0), 1);
      const f = pp * (shapes.length - 1);
      const idx = Math.min(Math.floor(f), shapes.length - 2);
      const m = smooth(f - idx);
      const a = shapes[idx], b = shapes[idx + 1];
      const pos = geo.attributes.position.array;
      for (let i = 0; i < COUNT; i++) {
        const i3 = i * 3;
        pos[i3] = a[i3] + (b[i3] - a[i3]) * m + Math.sin(t * speeds[i] + phases[i]) * 0.035;
        pos[i3 + 1] = a[i3 + 1] + (b[i3 + 1] - a[i3 + 1]) * m + Math.cos(t * speeds[i] * 0.8 + phases[i]) * 0.035;
        pos[i3 + 2] = a[i3 + 2] + (b[i3 + 2] - a[i3 + 2]) * m;
      }
      geo.attributes.position.needsUpdate = true;
      points.rotation.y = Math.sin(t * 0.1) * 0.05;
      renderer.render(scene, camera);
    };
    tick();

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="fixed inset-0 z-0 pointer-events-none hidden md:block" data-testid="section-particles-canvas" />;
};
